import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ShoppingBag, Trophy, Heart, Star } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

export default function Marketplace() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [points, setPoints] = useState(0);

  useEffect(() => {
    api.get("/marketplace").then((r) => setProducts(r.data));
    api.get("/dashboard").then((r) => setPoints(r.data.points));
  }, []);

  const buy = async (p) => {
    try {
      const r = await api.post("/payments/checkout", { product_id: p.id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error("Could not start checkout"); }
  };

  const bestDiscount = (p) =>
    p.discount_tiers?.filter((t) => t.points <= points).sort((a, b) => b.discount_pct - a.discount_pct)[0];

  return (
    <PhoneFrame>
      <div className="ps-aurora min-h-screen">
        <div className="px-5 pt-6 pb-28 relative">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="market-back">
            <ChevronLeft size={16} /> Back
          </button>

          <div className="mt-4 ps-fade-up">
            <div className="flex items-center justify-between">
              <div className="ps-chip">
                <ShoppingBag size={11} /> Curated rewards
              </div>
              <div className="px-3 py-1.5 rounded-full bg-[#15303E]/80 backdrop-blur border border-[#1B364A] inline-flex items-center gap-1.5 text-xs font-bold">
                <Trophy size={11} color="#4ade80" />
                <span className="ps-tabular">{points}</span>
              </div>
            </div>
            <h1 className="ps-heading text-4xl font-extrabold mt-3 leading-[1]">
              The <span className="ps-serif ps-italic font-normal ps-gradient-text">marketplace</span>.
            </h1>
            <p className="text-slate-400 text-sm mt-2 ps-serif ps-italic">Earn Posture Points™. Unlock discounts. Walk-and-stand better.</p>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 ps-stagger">
            {products.map((p) => {
              const disc = bestDiscount(p);
              const final = disc ? p.price_usd * (1 - disc.discount_pct / 100) : p.price_usd;
              return (
                <div key={p.id} className="ps-card overflow-hidden flex flex-col group relative" data-testid={`product-${p.id}`}>
                  <div className="aspect-square bg-[#15303E] relative overflow-hidden">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {disc && (
                      <div className="absolute top-2 left-2 ps-chip ps-chip-amber text-[9px] tracking-wider py-1 px-2">
                        −{disc.discount_pct}%
                      </div>
                    )}
                    <button className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center">
                      <Heart size={14} />
                    </button>
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="font-bold ps-heading text-sm leading-snug line-clamp-2">{p.name}</div>
                    <div className="text-[11px] text-slate-400 mt-1.5 line-clamp-2 flex-1 ps-serif ps-italic">{p.description}</div>
                    <div className="mt-2.5 flex items-baseline gap-2">
                      <div className="font-extrabold ps-gradient-text ps-tabular text-lg">${final.toFixed(2)}</div>
                      {disc && <div className="text-[10px] text-slate-500 line-through ps-tabular">${p.price_usd.toFixed(2)}</div>}
                    </div>
                    {disc ? (
                      <div className="text-[9px] text-[#FBBF24] mt-0.5 inline-flex items-center gap-1">
                        <Star size={9} fill="#FBBF24" /> {disc.points} pts tier
                      </div>
                    ) : p.discount_tiers?.[0] ? (
                      <div className="text-[9px] text-slate-500 mt-0.5">
                        Earn {p.discount_tiers[0].points} pts for {p.discount_tiers[0].discount_pct}% off
                      </div>
                    ) : null}
                    <button
                      data-testid={`buy-${p.id}`}
                      onClick={() => buy(p)}
                      className="mt-2.5 ps-button-primary text-xs py-2 px-3 inline-flex items-center justify-center gap-1"
                    >
                      <ShoppingBag size={12} /> Buy
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
